import React, { createContext, useContext, useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import {
  createRoutesFromChildren,
  useRoutes,
  useLocation,
  UNSAFE_RouteContext,
  type RouteObject,
  type RoutesProps,
} from "react-router-dom";
import { hasAuthenticatedSession, pageLifecycle } from "./faro";
import { registerRouteName } from "./route-names";
import { entityIds } from "./measurements";

interface AppRouteContext {
  appId?: string;
  customerRoutes?: boolean;
}

const AppContext = createContext<AppRouteContext>({});

function NamedRoute({ children }: { children: React.ReactNode }) {
  const { matches } = useContext(UNSAFE_RouteContext);
  const location = useLocation();
  const app = useContext(AppContext);
  const authenticated = useSelector(hasAuthenticatedSession);

  // RouteContext contains the source route objects, including ancestor routes.
  // Names stay template-based. Only explicit app/customer IDs are separate fields.
  const patterns = matches.map((match) => match.route.path || "");
  const name = registerRouteName(patterns);
  const customerId = app.customerRoutes ? matches.at(-1)?.params.licenseId : undefined;

  useLayoutEffect(() => {
    pageLifecycle.routeName(
      name,
      matches.length,
      entityIds({ app_id: app.appId, customer_id: customerId })
    );
  }, [name, matches.length, location.key, app.appId, customerId, authenticated]);

  return <>{children}</>;
}

function instrument(routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => {
    const instrumentedRoute = { ...route };

    if (route.element) {
      instrumentedRoute.element = <NamedRoute>{route.element}</NamedRoute>;
    }

    if (route.children) {
      instrumentedRoute.children = instrument(route.children);
    }

    return instrumentedRoute;
  });
}

// Keep React Router's matching and rendering behavior; decorate matched elements.
export function Routes({
  children,
  location,
  appId,
  customerRoutes,
}: RoutesProps & AppRouteContext) {
  const parent = useContext(AppContext);
  const definitions = createRoutesFromChildren(children);
  const routes = useRoutes(
    import.meta.env.VITE_FARO_ENDPOINT ? instrument(definitions) : definitions,
    location
  );
  return (
    <AppContext.Provider value={appId !== undefined ? { appId, customerRoutes } : parent}>
      {routes}
    </AppContext.Provider>
  );
}
